import {
  PlusOutlined,
  ReloadOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import { useLazyQuery, useMutation } from '@apollo/client';
import {
  Button,
  Card,
  Empty,
  message,
  Popconfirm,
  Skeleton,
  Tooltip,
} from 'antd';
import React, { useEffect, useRef, useState } from 'react';
import {
  ADD_ULTRA_PLAN_ROADMAP_WEEK,
  GENERATE_ULTRA_PLAN_ROADMAP,
  REVIEW_ROADMAP_TOY_SWAP_SUGGESTION,
  SWAP_ULTRA_PLAN_ROADMAP_SLOTS,
  UPSERT_ULTRA_PLAN_ROADMAP_SLOT,
} from '../../../../graphql/Mutations';
import { ULTRA_PLAN_ROADMAP } from '../../../../graphql/Queries';
import useToyScheduleOrders, {
  isOrderItemFailed,
} from '../../../../hooks/useToyScheduleOrders';
import {
  INITIAL_ROADMAP_WEEKS,
  ROADMAP_ERROR_CODES,
  SWAP_SUGGESTION_DECISION,
  ULTRA_PLAN_ORDERS_ENABLED,
} from '../../../../ultraPlanConstants';
import {
  canWeekAddBigToy,
  getBigToyLimitMessage,
  getFullName,
  getRoadmapErrorMessage,
  getToyTypeSlot,
  sortWeeks,
} from '../../../../ultraPlanUtils';
import GenerateRoadmapModal from './GenerateRoadmapModal';
import PlaceWeekOrderModal from './PlaceWeekOrderModal';
import RoadmapContextCard from './RoadmapContextCard';
import RoadmapLockNotice from './RoadmapLockNotice';
import RoadmapSlotModal from './RoadmapSlotModal';
import RoadmapWeek, { getWeekElementId } from './RoadmapWeek';
import RoadmapWeekNav from './RoadmapWeekNav';
import SwapSlotModal from './SwapSlotModal';

const scrollToWeek = (weekNumber) =>
  // eslint-disable-next-line no-undef
  document
    ?.getElementById(getWeekElementId(weekNumber))
    ?.scrollIntoView({ behavior: 'smooth', block: 'start' });

const RoadmapPlanner = ({
  userSubscriptionId,
  subscription,
  defaultChild,
  childProfiles = [],
  isChildProfilesLoading,
  hasChildProfilesError,
  onOpenSubscriber,
}) => {
  const [roadmap, setRoadmap] = useState(null);
  const [hasLoadError, setHasLoadError] = useState(false);
  const [isDefaultChildRequired, setIsDefaultChildRequired] = useState(false);
  const [isRoadmapLoaded, setIsRoadmapLoaded] = useState(false);
  const [slotTarget, setSlotTarget] = useState(null);
  const [swapSource, setSwapSource] = useState(null);
  const [isSwapping, setIsSwapping] = useState(false);
  const [orderWeek, setOrderWeek] = useState(null);
  const [isGenerateOpen, setIsGenerateOpen] = useState(false);
  const wasChildMissingRef = useRef(false);

  const userNumber = subscription?.user?.userNumber;
  const { loading: isProcessingOrder, placeWeekOrder } = useToyScheduleOrders({
    userNumber,
  });
  const canManageOrders = ULTRA_PLAN_ORDERS_ENABLED && !!userNumber;

  const [fetchRoadmap, { loading: isRoadmapLoading }] = useLazyQuery(
    ULTRA_PLAN_ROADMAP,
    {
      fetchPolicy: 'network-only',
      onCompleted: (response) => {
        setRoadmap(response?.ultraPlanRoadmap?.data || null);
        setHasLoadError(false);
        setIsDefaultChildRequired(false);
        setIsRoadmapLoaded(true);
      },
      onError(error) {
        const isChildRequired =
          error?.graphQLErrors?.[0]?.extensions?.code ===
          ROADMAP_ERROR_CODES?.DEFAULT_CHILD_REQUIRED;
        setRoadmap(null);
        setIsDefaultChildRequired(isChildRequired);
        setHasLoadError(!isChildRequired);
        if (!isChildRequired) {
          message?.error(getRoadmapErrorMessage(error));
        }
        setIsRoadmapLoaded(true);
      },
    },
  );

  const [
    generateRoadmap,
    { loading: isGenerating },
  ] = useMutation(GENERATE_ULTRA_PLAN_ROADMAP, { onError() {} });
  const [
    addRoadmapWeek,
    { loading: isAddingWeek },
  ] = useMutation(ADD_ULTRA_PLAN_ROADMAP_WEEK, { onError() {} });
  const [
    upsertRoadmapSlot,
    { loading: isSavingSlot },
  ] = useMutation(UPSERT_ULTRA_PLAN_ROADMAP_SLOT, { onError() {} });
  const [
    swapRoadmapSlots,
    { loading: isSwappingSlots },
  ] = useMutation(SWAP_ULTRA_PLAN_ROADMAP_SLOTS, { onError() {} });
  const [reviewSwapSuggestion] = useMutation(
    REVIEW_ROADMAP_TOY_SWAP_SUGGESTION,
    { onError() {} },
  );

  const loadRoadmap = () =>
    fetchRoadmap({
      variables: {
        data: { userSubscriptionId },
      },
    });

  useEffect(() => {
    setRoadmap(null);
    setHasLoadError(false);
    setIsRoadmapLoaded(false);
    if (userSubscriptionId) loadRoadmap();
  }, [userSubscriptionId]);

  const weeks = sortWeeks(roadmap?.weeks);
  const currentWeekNumber =
    roadmap?.currentWeekNumber ||
    weeks?.find((week) => week?.isCurrentWeek)?.weekNumber ||
    null;
  const hasWeeks = weeks?.length > 0;
  const nextWeekNumber = (weeks?.[weeks.length - 1]?.weekNumber || 0) + 1;
  const maxWeeks = subscription?.planDurationWeeks;
  const isWeekLimitReached = !!maxWeeks && nextWeekNumber > maxWeeks;
  const maxBigToys = subscription?.subscriptionFeatures?.maxBigToyAllowed;
  const slotBigToyBlockedReason =
    slotTarget &&
    !canWeekAddBigToy({
      week: weeks?.find((week) => week?.weekNumber === slotTarget?.weekNumber),
      replacedSlotId: slotTarget?.slot?.id,
      maxBigToys,
    })
      ? getBigToyLimitMessage(maxBigToys, slotTarget?.weekNumber)
      : null;
  const isMutating =
    isGenerating ||
    isAddingWeek ||
    isSavingSlot ||
    isSwapping ||
    isSwappingSlots ||
    isProcessingOrder;
  const childName = defaultChild?.firstName || getFullName(defaultChild);
  const isMissingDefaultChild =
    isDefaultChildRequired ||
    (!isChildProfilesLoading && !hasChildProfilesError && !defaultChild);
  const hasActiveChildren = childProfiles?.some((child) => child?.isActive);
  const isEditLocked = isChildProfilesLoading || !defaultChild;
  let lockReason = null;
  if (hasChildProfilesError) {
    lockReason =
      'Children couldn’t be loaded. Reopen the roadmap to try again.';
  } else if (isMissingDefaultChild) {
    lockReason = hasActiveChildren
      ? 'Set a default child to make changes'
      : 'Add a child to make changes';
  }

  useEffect(() => {
    if (isMissingDefaultChild) wasChildMissingRef.current = true;
  }, [isMissingDefaultChild]);

  useEffect(() => {
    if (!defaultChild?.id || !wasChildMissingRef.current) return;
    wasChildMissingRef.current = false;
    loadRoadmap();
    message?.success(
      `${childName || 'Default child'} is set. You can manage the roadmap now.`,
    );
  }, [defaultChild?.id]);

  const applyRoadmapResponse = (roadmapData, successMessage) => {
    if (!roadmapData) return false;
    setRoadmap(roadmapData);
    message?.success(successMessage);
    return true;
  };

  const handleGenerate = async (preferenceCodes = []) => {
    const response = await generateRoadmap({
      variables: {
        data: preferenceCodes?.length
          ? {
              userSubscriptionId,
              childProfileId: defaultChild?.id,
              preferenceCodes,
            }
          : { userSubscriptionId },
      },
    });
    const isGenerated = applyRoadmapResponse(
      response?.data?.generateUltraPlanRoadmap?.data,
      `Roadmap ready: ${INITIAL_ROADMAP_WEEKS} weeks planned for ${childName}.`,
    );
    if (isGenerated) setIsGenerateOpen(false);
  };

  const handleAddWeek = async () => {
    if (isWeekLimitReached) return;
    const response = await addRoadmapWeek({
      variables: { data: { userSubscriptionId } },
    });
    const roadmapData = response?.data?.addUltraPlanRoadmapWeek?.data;
    const lastWeekNumber = sortWeeks(roadmapData?.weeks)?.pop()?.weekNumber;
    if (applyRoadmapResponse(roadmapData, `Week ${lastWeekNumber} added.`)) {
      setTimeout(() => scrollToWeek(lastWeekNumber), 100);
    }
  };

  const saveSlotToy = async ({ weekNumber, position, slot, toyId }) => {
    const response = await upsertRoadmapSlot({
      variables: {
        data: {
          userSubscriptionId,
          childProfileId: defaultChild?.id,
          weekNumber,
          slotPosition: slot?.slotPosition || position,
          toyId,
        },
      },
    });
    return response?.data?.upsertUltraPlanRoadmapSlot?.data || null;
  };

  const handleSaveSlot = async (toyId) => {
    const { weekNumber, slotConfig } = slotTarget || {};
    const roadmapData = await saveSlotToy({ ...slotTarget, toyId });
    const isSaved = applyRoadmapResponse(
      roadmapData,
      `Week ${weekNumber} ${slotConfig?.label || 'toy'} updated.`,
    );
    if (isSaved) setSlotTarget(null);
  };

  const handleApproveSuggestion = async (suggestion) => {
    const { weekNumber, slotConfig, slot } = swapSource || {};
    if (!suggestion?.id || !slot?.id) return;
    setIsSwapping(true);
    try {
      const response = await reviewSwapSuggestion({
        variables: {
          data: {
            suggestionId: suggestion?.id,
            toyScheduleItemId: slot?.id,
            decision: SWAP_SUGGESTION_DECISION?.APPROVE,
          },
        },
      });
      if (!response?.data?.reviewRoadmapToySwapSuggestion?.data) return;
      message?.success(
        `Week ${weekNumber} ${slotConfig?.label} replaced with ${suggestion?.suggestedToy?.name}.`,
      );
      setSwapSource(null);
      loadRoadmap();
    } finally {
      setIsSwapping(false);
    }
  };

  const handleSwapSlot = async (target) => {
    const { weekNumber, slotConfig, slot } = swapSource || {};
    const targetSlot = target?.targetSlot;
    if (!slot?.id || !targetSlot?.id) return;

    const response = await swapRoadmapSlots({
      variables: {
        data: {
          userSubscriptionId,
          sourceSlotId: slot?.id,
          targetSlotId: targetSlot?.id,
        },
      },
    });
    const isSwapped = applyRoadmapResponse(
      response?.data?.swapUltraPlanRoadmapSlots?.data,
      `Week ${weekNumber} ${slotConfig?.label || 'toy'} swapped with week ${
        target?.weekNumber
      } ${getToyTypeSlot(targetSlot?.toy)?.label || 'toy'}.`,
    );
    if (isSwapped) setSwapSource(null);
  };

  const handlePlaceOrder = async (payload) => {
    const items = await placeWeekOrder(payload);
    if (items?.some((item) => !isOrderItemFailed(item))) loadRoadmap();
    return items;
  };

  const renderAddWeekButton = (buttonProps = {}) =>
    isWeekLimitReached ? (
      <Tooltip title="Every week of this plan is already planned.">
        <Button icon={<PlusOutlined />} disabled {...buttonProps}>
          Add Week
        </Button>
      </Tooltip>
    ) : (
      <Popconfirm
        title={`Add week ${nextWeekNumber}?`}
        description="Toys for the new week are picked automatically. You can change them after. No orders are placed."
        okText="Add Week"
        cancelText="Cancel"
        onConfirm={() => {
          handleAddWeek();
        }}
        disabled={isMutating || isEditLocked}
      >
        <Tooltip title={lockReason}>
          <Button
            icon={<PlusOutlined />}
            loading={isAddingWeek}
            disabled={(isMutating && !isAddingWeek) || isEditLocked}
            {...buttonProps}
          >
            {`Add Week ${nextWeekNumber}`}
          </Button>
        </Tooltip>
      </Popconfirm>
    );

  const renderDefaultChildNotice = () => {
    if (!isMissingDefaultChild) return null;
    return (
      <RoadmapLockNotice
        hasRoadmap={hasWeeks}
        hasActiveChildren={hasActiveChildren}
        onAction={onOpenSubscriber}
      />
    );
  };

  const renderWeeks = () => {
    if (!isRoadmapLoaded || (isRoadmapLoading && !roadmap)) {
      return (
        <Card>
          <Skeleton active paragraph={{ rows: 6 }} />
        </Card>
      );
    }
    if (isDefaultChildRequired) return null;
    if (hasLoadError) {
      return (
        <Card>
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="The roadmap couldn't be loaded."
          >
            <Button icon={<ReloadOutlined />} onClick={loadRoadmap}>
              Retry
            </Button>
          </Empty>
        </Card>
      );
    }
    if (!hasWeeks) {
      return (
        <Card>
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <>
                <div className="roadmap-empty-title">
                  No roadmap for this subscription yet
                </div>
                <div className="ultra-plan-muted">
                  {`Generate weeks 1–${INITIAL_ROADMAP_WEEKS} with toys and books picked for ${
                    childName ? `${childName}'s` : "the default child's"
                  } age and library. You can change any toy afterwards. Nothing is ordered.`}
                </div>
              </>
            }
          >
            <Tooltip title={lockReason}>
              <Button
                type="primary"
                size="large"
                icon={<ThunderboltOutlined />}
                disabled={isMutating || isEditLocked}
                onClick={() => setIsGenerateOpen(true)}
              >
                Generate Roadmap
              </Button>
            </Tooltip>
          </Empty>
        </Card>
      );
    }
    return (
      <>
        <div className="roadmap-toolbar">
          <RoadmapWeekNav weeks={weeks} />
          {renderAddWeekButton({ type: 'primary' })}
        </div>
        {weeks?.map((week) => (
          <RoadmapWeek
            key={week?.weekNumber}
            week={week}
            currentWeekNumber={currentWeekNumber}
            disabled={isMutating}
            isEditDisabled={isEditLocked}
            lockReason={lockReason}
            onEditSlot={setSlotTarget}
            onSwapSlot={setSwapSource}
            onPlaceOrder={canManageOrders ? setOrderWeek : undefined}
          />
        ))}
        {!isWeekLimitReached && (
          <div className="roadmap-add-week-footer">{renderAddWeekButton()}</div>
        )}
      </>
    );
  };

  return (
    <div className="ultra-plan-drawer-content">
      <RoadmapContextCard
        subscription={subscription}
        defaultChild={defaultChild}
        isChildLoading={isChildProfilesLoading}
        hasChildError={hasChildProfilesError}
        roadmap={roadmap}
        weeks={weeks}
        onOpenSubscriber={onOpenSubscriber}
      />
      {renderDefaultChildNotice()}
      {renderWeeks()}
      <GenerateRoadmapModal
        open={isGenerateOpen}
        childName={childName}
        loading={isGenerating}
        onCancel={() => setIsGenerateOpen(false)}
        onGenerate={handleGenerate}
      />
      <SwapSlotModal
        swapSource={swapSource}
        userSubscriptionId={userSubscriptionId}
        childProfileId={defaultChild?.id}
        weeks={weeks}
        maxBigToys={maxBigToys}
        loading={isSwapping || isSwappingSlots}
        onCancel={() => setSwapSource(null)}
        onSwap={handleSwapSlot}
        onApproveSuggestion={handleApproveSuggestion}
      />
      <RoadmapSlotModal
        slotTarget={slotTarget}
        userId={subscription?.user?.id}
        childName={childName}
        bigToyBlockedReason={slotBigToyBlockedReason}
        loading={isSavingSlot}
        onCancel={() => setSlotTarget(null)}
        onSave={handleSaveSlot}
      />
      {canManageOrders && (
        <PlaceWeekOrderModal
          week={orderWeek}
          userNumber={userNumber}
          loading={isProcessingOrder}
          onPlaceOrder={handlePlaceOrder}
          onCancel={() => setOrderWeek(null)}
        />
      )}
    </div>
  );
};

export default RoadmapPlanner;
